<template>
  <div class="security-guide-page">
    <HeadNav selected-language="en" page="security-guide" />

    <main class="guide-main">
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Security Guide</span>
      </nav>

      <header class="guide-header">
        <h1>GB WhatsApp Security Guide</h1>
        <p>
          GB WhatsApp is an unofficial modified application. Before installing or using it, understand the potential security, privacy and account-related risks. This page covers key safety topics and provides further reading on common issues.
        </p>
      </header>

      <section class="topic-grid" aria-label="Security topics">
        <article v-for="topic in topics" :key="topic.title" class="topic-card">
          <span class="topic-icon" aria-hidden="true">{{ topic.icon }}</span>
          <h2>{{ topic.title }}</h2>
          <p>{{ topic.description }}</p>
        </article>
      </section>

      <section class="articles-section" aria-label="Security articles">
        <div class="article-cards">
          <a
            v-for="article in articles"
            :key="article.slug"
            :href="article.path"
            class="article-card"
          >
            <h3>{{ article.h1 }}</h3>
            <p>{{ article.cardSummary }}</p>
            <span class="read-link">Read Article</span>
          </a>
        </div>
      </section>

      <p class="disclaimer">
        <a href="/">GBWhatsDroid.com</a> is an independent informational website and is not affiliated with WhatsApp or Meta. Verify files and compatibility details before installing any APK.
      </p>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';
import securityArticles from '@/constants/securityArticles';

export default {
  name: 'SecurityGuide',
  components: { HeadNav },
  data() {
    return {
      articles: securityArticles,
      topics: [
        {
          icon: '🛡',
          title: 'App Safety',
          description: 'Understand the potential risks of modified apps and what to check before installing an APK.',
        },
        {
          icon: '⚠',
          title: 'Account Restrictions',
          description: 'Learn why unofficial WhatsApp clients may encounter warnings or account restrictions.',
        },
        {
          icon: '🔑',
          title: 'Login & Access Issues',
          description: 'Understand common access warnings and the options to consider when GB WhatsApp stops working normally.',
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.security-guide-page {
  min-height: 100vh;
  background: #f5f8f7;
  color: #14213d;
}

.guide-main {
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  padding: 24px 0 64px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 18px;
  color: #64748b;
  font-size: 13px;

  a {
    color: #0b6d47;
    font-weight: 600;
    text-decoration: none;
  }
}

.guide-header {
  padding: 38px;
  border: 1px solid #dcebe4;
  border-radius: 8px;
  background: linear-gradient(120deg, rgba(255, 255, 255, 0.96), rgba(237, 249, 243, 0.96));
  box-shadow: 0 10px 28px rgba(23, 70, 52, 0.06);

  h1 {
    margin: 0 0 12px;
    color: #112a21;
    font-size: clamp(32px, 5vw, 48px);
    line-height: 1.12;
  }

  p {
    margin: 0;
    max-width: none;
    color: #41554d;
    font-size: 17px;
    line-height: 1.75;
  }
}

.topic-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 24px;
}

.topic-card {
  padding: 22px;
  border: 1px solid #dfe9e4;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(28, 67, 53, 0.04);

  h2 {
    margin: 10px 0 8px;
    color: #173b2e;
    font-size: 20px;
  }

  p {
    margin: 0;
    color: #5d6c66;
    font-size: 15px;
    line-height: 1.65;
  }
}

.topic-icon {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #eaf7f1;
  font-size: 20px;
}

.articles-section {
  margin-top: 28px;
}

.article-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.article-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 22px;
  border: 1px solid #dfe9e4;
  border-radius: 8px;
  background: #fff;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  h3 {
    margin: 0 0 10px;
    color: #14213d;
    font-size: 18px;
    line-height: 1.4;
  }

  p {
    flex: 1;
    margin: 0 0 14px;
    color: #5d6c66;
    font-size: 14px;
    line-height: 1.65;
  }

  &:hover,
  &:focus {
    border-color: #b8dcc9;
    box-shadow: 0 8px 22px rgba(28, 67, 53, 0.08);
    transform: translateY(-2px);

    .read-link {
      text-decoration: underline;
    }
  }
}

.read-link {
  color: #0b6d47;
  font-weight: 800;
}

.disclaimer {
  margin: 28px 0 0;
  color: #5d6c66;
  font-size: 14px;
  line-height: 1.65;

  a {
    color: #2563eb !important;
    font-weight: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover,
    &:focus {
      color: #1d4ed8 !important;
    }
  }
}

@media (max-width: 900px) {
  .topic-grid,
  .article-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .guide-header {
    padding: 26px 20px;
  }
}
</style>
